import { useQuery } from '@apollo/client/react';
import type { TimelineResponseData } from 'framework/ui/timeline';
import { AccessInfo } from '../accessInfoTypes';
import { RISK_MITIGATION_STRATEGY_STATUS } from './constants';

export const RISK_TIMELINE_TYPE = {
  SYSTEM_NOTE: 'SYSTEM_NOTE',
  USER_NOTE: 'USER_NOTE',
} as const;

type RiskTimelineTypeValue = (typeof RISK_TIMELINE_TYPE)[keyof typeof RISK_TIMELINE_TYPE];

export interface RiskTimelineItem {
  id?: string;
  text?: string;
  type?: RiskTimelineTypeValue;
  attachmentPaths?: string[];
  isPinned?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

const RISK_TYPE = {
  PROJECT_MANAGEMENT: 'PROJECT_MANAGEMENT',
  ENGINEERING: 'ENGINEERING',
  COMMERCIAL: 'COMMERCIAL',
  STAFFING: 'STAFFING',
} as const;

type RiskTypeValue = (typeof RISK_TYPE)[keyof typeof RISK_TYPE];

export interface RisksType {
  id: string;
  alias?: string;
  title?: string;
  description?: string;
  type?: RiskTypeValue;
  status?: string;
  probability?: string;
  severity?: string;
  totalScore?: number;
  projectId?: number;
  riskOwner?: string;
  technicalNote?: string;
  mitigationStrategy?: string;
  mitigationStrategyStatus?: (typeof RISK_MITIGATION_STRATEGY_STATUS)[keyof typeof RISK_MITIGATION_STRATEGY_STATUS];
  isIncludedInCustomerReport?: boolean;
  isEscalated?: boolean;
  resolvedAt?: Date;
  resolveComment?: string;
  timeline?: RiskTimelineItem[];
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type NotePayload = {
  text: string;
  attachmentPaths?: string[];
};

export type RiskNote = {
  id?: string;
  payload?: NotePayload;
};

export interface ProjectRisksResponse {
  baseProject: {
    id: string | number;
    name?: string;
    risks: RisksType[];
    accessInfo?: AccessInfo;
  };
}

export interface ProjectAccountOwnerResponse {
  baseProject: {
    accountData?: {
      ownerAlias?: string;
    };
  };
}

export interface UseRiskByIdResult {
  risk: RisksType | null;
  projectName?: string;
  permissions?: AccessInfo;
  loading: boolean;
  error: ReturnType<typeof useQuery<ProjectRisksResponse>>['error'];
  refetch: ReturnType<typeof useQuery<ProjectRisksResponse>>['refetch'];
}

export type TimelineFilterRequest = {
  filter?: {
    type?: {
      [key: string]: string[];
    };
  };
};

export type TimelineCreateRequest = {
  text: string;
  attachmentPaths?: string[];
};

export interface UseRiskTimelineResult {
  data: RisksType | null;
  projectName?: string;
  permissions?: AccessInfo;
  loading: boolean;
  error: UseRiskByIdResult['error'];
  refetch: UseRiskByIdResult['refetch'];
  getTimelineItems: (_entityId: string, request: TimelineFilterRequest) => Promise<TimelineResponseData>;
  getTimelinePageById: (_entityId: string, _entryId: string) => Promise<TimelineResponseData>;
  createTimelineItem: (
    riskIdParam: string,
    _itemType: string,
    request: TimelineCreateRequest,
  ) => Promise<RiskTimelineItem & { timelineId: string }>;
  pinTimelineAction: (entityId: StringOrNumber, timelineId: StringOrNumber, pinStatus: boolean) => Promise<object>;
}
